
import { Slider } from "@/components/ui/slider";
import { useLocationServices } from "@/hooks/useLocationServices";
import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, MapPin, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

interface DistanceFilterProps {
  distance: number;
  setDistance: (value: number[]) => void;
}

export const DistanceFilter = ({
  distance,
  setDistance,
}: DistanceFilterProps) => {
  const { toast } = useToast();
  const { 
    locationState, 
    requestLocation, 
    shouldShowLocationPrompt, 
    recordPromptShown,
    refreshLocation,
    doNotShowAgain
  } = useLocationServices();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sessionPromptShown, setSessionPromptShown] = useState(false);
  
  // Format distance with appropriate units
  const formatFilterDistance = (dist: number): string => {
    if (dist === 0) return 'No limit';
    if (dist < 1) return `${dist * 10}/10 mi`;
    return `${dist} mi`;
  };
  
  // Refresh location when distance filter is interacted with
  useEffect(() => {
    if (locationState.permission === 'granted' && locationState.coords && !isRefreshing && !locationState.isLoading) {
      // Check if location needs refreshing - this is just to ensure we have fresh location
      // when user is actively using distance filters
      const maybeRefresh = async () => {
        if (!locationState.lastUpdated) return;
        
        const now = Date.now();
        const ageInMinutes = (now - locationState.lastUpdated) / (60 * 1000);
        
        // Refresh if location is older than 5 minutes
        if (ageInMinutes > 5) {
          setIsRefreshing(true);
          await refreshLocation();
          setIsRefreshing(false);
        }
      };
      
      maybeRefresh();
    }
  }, [distance, locationState.permission, locationState.coords, locationState.lastUpdated, locationState.isLoading, refreshLocation]);
  
  const handleLocationRequest = async () => {
    // Only show the prompt once per session in this component
    if (!sessionPromptShown && !doNotShowAgain && shouldShowLocationPrompt(true)) {
      recordPromptShown();
      setSessionPromptShown(true);
    }
    
    setIsRefreshing(true);
    const result = await requestLocation();
    setIsRefreshing(false);
    
    // Show appropriate toast based on result
    if (result) {
      toast({
        title: "Location services enabled",
        description: "Distance filtering is now available"
      });
    }
  };
  
  // Improved check that accounts for transitioning states and existing coordinates
  const needsLocation = 
    (locationState.permission !== 'granted') || 
    !locationState.coords ||
    locationState.permissionTransitioning;
    
  const isLoading = isRefreshing || locationState.isLoading || locationState.permissionTransitioning;

  // Only show location active when we actually have coordinates and permission
  const hasActiveLocation = 
    locationState.permission === 'granted' && 
    locationState.coords && 
    !isLoading && 
    !locationState.permissionTransitioning;

  return (
    <div>
      <h3 className="text-sm font-medium mb-2 flex items-center justify-between">
        <span>Distance {distance ? `(${formatFilterDistance(distance)})` : '(No limit)'}</span>
        
        {isLoading && (
          <span className="text-xs flex items-center text-muted-foreground">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Updating...
          </span>
        )}
        
        {hasActiveLocation && (
          <span className="text-xs flex items-center text-green-600">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Location active
          </span>
        )}
      </h3>
      
      <Slider
        max={25}
        step={1}
        value={[distance]}
        onValueChange={setDistance}
        className={needsLocation ? "opacity-70" : ""}
      />
      
      {/* Show the location button when location is needed but not being refreshed */}
      {locationState.permission === 'prompt' && !isLoading && (
        <div className="mt-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full text-xs flex items-center justify-center"
            onClick={handleLocationRequest}
            disabled={isLoading}
          >
            <MapPin className="h-3 w-3 mr-1" />
            Enable location for accurate distances
          </Button>
        </div>
      )}
      
      {locationState.permission === 'denied' && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 text-amber-500 flex-shrink-0" />
          <div>
            <p>Location access denied. Enable it in your browser settings for distance filtering.</p>
            <Button 
              variant="link" 
              size="sm" 
              className="h-auto p-0 text-xs"
              onClick={() => window.open('about:preferences', '_blank')}
            >
              Open settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
